/**
 * ConditionEvaluator - Declarative condition system (T-TL-34 Enhanced)
 *
 * Evaluates data-dr-show-when, data-dr-hide-when, and data-dr-effect-when
 * attributes against state from DreamRealityRuntime or cached section data.
 * Supports OR logic via pipe separator.
 *
 * Now supports universal state management:
 * - Tries DreamRealityRuntime first (if provided)
 * - Falls back to cached data (for preview mode backward compat)
 * - Works with any state path: hero.mediaType, pricing.flow, etc.
 *
 * Condition format: sectionId.field=value
 * OR logic:         sectionId.field=value1|sectionId.field=value2
 */

import type { DreamRealityRuntime } from '../src/dream-reality-runtime.js';

interface ParsedCondition {
  sectionId: string;
  field: string;
  value: string;
}

export class ConditionEvaluator {
  private runtime: DreamRealityRuntime | null = null;

  constructor(runtime?: DreamRealityRuntime) {
    this.runtime = runtime || null;
  }

  /**
   * Evaluate all declarative conditions against current state.
   * 
   * @param cachedData - Optional cached section data (for preview mode)
   */
  evaluateAll(cachedData?: Record<string, Record<string, any>>): void {
    this.evaluateShowWhen(cachedData);
    this.evaluateHideWhen(cachedData);
    this.evaluateEffects(cachedData);
  }

  /**
   * data-dr-show-when: element visible only when condition matches
   */
  private evaluateShowWhen(cachedData?: Record<string, Record<string, any>>): void {
    document.querySelectorAll('[data-dr-show-when]').forEach(el => {
      const attr = el.getAttribute('data-dr-show-when');
      if (!attr) return;
      const conditions = this.parseCondition(attr);
      const match = this.conditionMatches(conditions, cachedData);
      (el as HTMLElement).style.display = match ? '' : 'none';
    });
  }

  /**
   * data-dr-hide-when: element hidden when condition matches
   */
  private evaluateHideWhen(cachedData?: Record<string, Record<string, any>>): void {
    document.querySelectorAll('[data-dr-hide-when]').forEach(el => {
      const attr = el.getAttribute('data-dr-hide-when');
      if (!attr) return;
      const conditions = this.parseCondition(attr);
      const match = this.conditionMatches(conditions, cachedData);
      (el as HTMLElement).style.display = match ? 'none' : '';
    });
  }

  /**
   * data-dr-effect-when + data-dr-effect: apply/remove CSS when condition matches
   */
  private evaluateEffects(cachedData?: Record<string, Record<string, any>>): void {
    document.querySelectorAll('[data-dr-effect-when]').forEach(el => {
      const attr = el.getAttribute('data-dr-effect-when');
      if (!attr) return;
      const effect = el.getAttribute('data-dr-effect') || '';
      const conditions = this.parseCondition(attr);
      const match = this.conditionMatches(conditions, cachedData);
      const htmlEl = el as HTMLElement;

      effect.split(';').filter(Boolean).forEach(rule => {
        const colonIndex = rule.indexOf(':');
        if (colonIndex === -1) return;
        const prop = rule.substring(0, colonIndex).trim();
        const val = rule.substring(colonIndex + 1).trim();
        if (prop) {
          (htmlEl.style as any)[prop] = match ? val : '';
        }
      });
    });
  }

  /**
   * Parse a condition string into structured conditions.
   * Supports OR via pipe: "hero.mediaType=image|hero.mediaType=carousel"
   */
  private parseCondition(attr: string): ParsedCondition[] {
    return attr.split('|').map(cond => {
      const trimmed = cond.trim();
      const eqIndex = trimmed.indexOf('=');
      if (eqIndex === -1) {
        console.warn('[ConditionEvaluator] Invalid condition format:', trimmed);
        return { sectionId: '', field: '', value: '' };
      }
      const path = trimmed.substring(0, eqIndex);
      const value = trimmed.substring(eqIndex + 1);
      const dotIndex = path.indexOf('.');
      if (dotIndex === -1) {
        console.warn('[ConditionEvaluator] Invalid condition path:', path);
        return { sectionId: '', field: '', value };
      }
      return {
        sectionId: path.substring(0, dotIndex),
        field: path.substring(dotIndex + 1),
        value,
      };
    });
  }

  /**
   * Check if any condition in the OR set matches the current state.
   * 
   * Priority:
   * 1. Try DreamRealityRuntime (if available)
   * 2. Fall back to cached data (for preview mode)
   * 3. Return false if neither available
   */
  private conditionMatches(
    conditions: ParsedCondition[],
    cachedData?: Record<string, Record<string, any>>
  ): boolean {
    return conditions.some(c => {
      // Skip invalid conditions
      if (!c.sectionId || !c.field) {
        return false;
      }

      // Try runtime first (universal state management)
      if (this.runtime) {
        const statePath = `${c.sectionId}.${c.field}`;
        const value = this.runtime.getState(statePath);
        if (value !== undefined) {
          return String(value) === c.value;
        }
      }

      // Fall back to cached data (preview mode backward compat)
      if (cachedData) {
        const fieldValue = cachedData[c.sectionId]?.[c.field];
        return String(fieldValue ?? '') === c.value;
      }

      // No state source available
      return false;
    });
  }
}
